import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAsync, useAsyncEffect } from "./async.js";
export const AppContext = createContext(undefined);
export function useAppContext() {
    const appContext = useContext(AppContext);
    if (!appContext) {
        throw new Error("No app context provided.");
    }
    return appContext;
}
export function usePlatform() {
    return useAppContext().platform;
}
export function useAppEventSupport() {
    return useAppContext().appEventSupport;
}
export function useActivateListener(activateHandler) {
    const appEventSupport = useAppEventSupport();
    useEffect(() => {
        appEventSupport.addActivateListener(activateHandler);
        return () => appEventSupport.removeActivateListener(activateHandler);
    }, [activateHandler]);
}
export function useDeactivateListener(deactivateHandler) {
    const appEventSupport = useAppEventSupport();
    useEffect(() => {
        appEventSupport.addDeactivateListener(deactivateHandler);
        return () => appEventSupport.removeDeactivateListener(deactivateHandler);
    }, [deactivateHandler]);
}
export function useCanAutoDeactivateListener(canAutoDeactivateHandler) {
    const appEventSupport = useAppEventSupport();
    useEffect(() => {
        appEventSupport.addCanAutoDeactivateListener(canAutoDeactivateHandler);
        return () => appEventSupport.removeCanAutoDeactivateListener(canAutoDeactivateHandler);
    }, [canAutoDeactivateHandler]);
}
export function usePreventAutoDeactivate() {
    useCanAutoDeactivateListener(useCallback(() => false, []));
}
export function usePreventDeactivate() {
    const appEventSupport = useAppEventSupport();
    useEffect(() => {
        appEventSupport.addDeactivateVeto();
        return () => appEventSupport.removeDeactivateVeto();
    }, [appEventSupport]);
}
export function useLocationCodeListener(locationCodeHandler) {
    const platform = usePlatform();
    useEffect(() => {
        platform.addLocationCodeListener(locationCodeHandler);
        return () => platform.removeLocationCodeListener(locationCodeHandler);
    }, [locationCodeHandler]);
}
export function useLocationCode() {
    const platform = usePlatform();
    const [locationCode, setLocationCode] = useState(platform.locationCode);
    useLocationCodeListener(setLocationCode);
    return locationCode;
}
function useStorageItemCommon(key, state) {
    const platform = usePlatform();
    const listener = useCallback(async (changedKey) => {
        console.log("storage listener", changedKey);
        if (changedKey == key) {
            await state.execute();
        }
    }, [platform, key, state.execute]);
    useEffect(() => {
        platform.addStorageListener(listener);
        return () => platform.removeStorageListener(listener);
    }, [platform, listener]);
    return state;
}
export function useStorageItem(key) {
    const platform = usePlatform();
    const state = useAsync(() => platform.getStorageItem(key), [platform, key]);
    return useStorageItemCommon(key, state);
}
export function useStorageItemValue(key) {
    return useStorageItem(key).result;
}
export function useStorageItemBlob(key) {
    const platform = usePlatform();
    const state = useAsync(() => platform.getStorageItemBlob(key), [platform, key]);
    return useStorageItemCommon(key, state).result;
}
export function useCardListener(listener) {
    const platform = usePlatform();
    useEffect(() => {
        platform.nfcReader.addCardListener(listener);
        return () => platform.nfcReader.removeCardListener(listener);
    }, [platform, listener]);
}
export function useLockStatus(lock) {
    const [status, setStatus] = useState();
    const platform = usePlatform();
    const listener = useCallback((changedLock, status) => {
        if (changedLock == lock) {
            setStatus(status);
        }
    }, [lock]);
    useEffect(() => {
        platform.addLockStatusListener(listener);
        return () => platform.removeLockStatusListener(listener);
    }, [platform, listener]);
    useAsyncEffect(async () => {
        setStatus(lock ? await platform.getLockStatus(lock) : undefined);
    }, [platform, lock]);
    return status;
}
export function useBoxLockStatus(boxNumber) {
    const platform = usePlatform();
    const { result } = useAsync(async () => await platform.getLock(boxNumber), [platform, boxNumber]);
    return useLockStatus(result);
}
export function useBarcodeListener(listener) {
    const eventSupport = useAppEventSupport();
    useEffect(() => {
        console.log("useBarcodeListener: registering listener");
        eventSupport.addBarcodeListener(listener);
        return () => {
            console.log("useBarcodeListener: unregistering listener");
            eventSupport.removeBarcodeListener(listener);
        };
    }, [eventSupport, listener]);
}
export function useKeypadListener(listener) {
    const eventSupport = useAppEventSupport();
    useEffect(() => {
        console.log("useKeypadListener: registering listener");
        eventSupport.addKeypadListener(listener);
        return () => {
            console.log("useKeypadListener: unregistering listener");
            eventSupport.removeKeypadListener(listener);
        };
    }, [eventSupport, listener]);
}
export function useAppAdmissionListener(listener) {
    const eventSupport = useAppEventSupport();
    useEffect(() => {
        console.log('useAppAdmissionListener: registering listener');
        eventSupport.addAppAdmissionListener(listener);
        return () => {
            console.log('useAppAdmissionListener: unregistering listener');
            eventSupport.removeAppAdmissionListener(listener);
        };
    }, [eventSupport, listener]);
}
export function useBoxes() {
    const platform = usePlatform();
    const [boxes, setBoxes] = useState(platform.boxes);
    useEffect(() => {
        platform.addBoxListener(setBoxes);
        return () => platform.removeBoxListener(setBoxes);
    }, [platform, setBoxes]);
    return boxes;
}
export function useBox(number) {
    const boxes = useBoxes();
    return useMemo(() => boxes.find(box => box.number == number), [boxes, number]);
}
export function useOccupancies() {
    const platform = usePlatform();
    const [occupancies, setOccupancies] = useState(platform.occupancies);
    useEffect(() => {
        // initialize occupancies again in case an occupancy event was received between
        // state initialization and the first run of this effect.
        setOccupancies(platform.occupancies);
        platform.addOccupancyListener(setOccupancies);
        return () => platform.removeOccupancyListener(setOccupancies);
    }, [platform, setOccupancies]);
    return occupancies;
}
export function useUtilization() {
    const platform = usePlatform();
    const [utilization, setUtilization] = useState(platform.utilization);
    useEffect(() => {
        platform.addUtilizationListener(setUtilization);
        return () => platform.removeUtilizationListener(setUtilization);
    }, [platform, setUtilization]);
    return utilization;
}
export function useDevices() {
    const platform = usePlatform();
    const [devices, setDevices] = useState(platform.devices);
    useEffect(() => {
        platform.addDeviceListener(setDevices);
        return () => platform.removeDeviceListener(setDevices);
    }, [platform, setDevices]);
    return devices;
}
export function useDevicePresent(type) {
    const devices = useDevices();
    return useMemo(() => devices.find(device => device.types.indexOf(type) >= 0), [devices, type]);
}
//# sourceMappingURL=context.js.map